import fortniteIcon from "../../img/filter-icons/logo-fn.svg";
import unrealIcon from "../../img/filter-icons/logo-ue.svg";
import choizIcon from "../../img/filter-icons/logo-choiz.svg";

import {
	getCaseFilterOptions,
	getFilterLabel,
	normalizeCaseFilter,
} from "./caseFilterHelpers";

/*
 * ============================================================
 * PRIMARY / SCOPE FILTERS
 * ============================================================
 *
 * These are still the fundamental case categories.
 *
 * They remain separate from the Sheet-driven topic filters.
 */
export const CASE_SCOPE_FILTERS = [
	{
		value: "all",
		label: "All",
	},
	{
		value: "work",
		label: "Work",
	},
	{
		value: "solution",
		label: "Solutions",
	},
];

/*
 * ============================================================
 * PREFERRED TOPIC FILTER ORDER
 * ============================================================
 *
 * Filters listed here appear first, in exactly this order.
 *
 * Everything not listed follows alphabetically.
 *
 * Both labels and slugs work:
 *
 * "Unreal Engine"
 * "unreal-engine"
 */
export const PREFERRED_FILTER_ORDER = [
	"Fortnite",
	"Choiz.io",
	"Unreal Engine",
];

const preferredFilterOrder =
	PREFERRED_FILTER_ORDER.map(
		normalizeCaseFilter,
	);

/*
 * ============================================================
 * FILTER PRESENTATION
 * ============================================================
 *
 * This is now shared by:
 *
 * - Header
 * - Solution slides
 * - future filter/tag UI
 *
 * mark
 * -> fallback text if an image cannot be loaded
 *
 * image
 * -> actual imported icon
 */
export const FILTER_PRESENTATION = {
	fortnite: {
		mark: "FN",
		image: fortniteIcon,
	},

	"choiz-io": {
		mark: "CH",
		image: choizIcon,
	},

	"unreal-engine": {
		mark: "UE",
		image: unrealIcon,
	},

	/*
	 * Optional category presentation:
	 *
	 * work: {
	 * 		mark: "W",
	 * 		image: workIcon,
	 * },
	 *
	 * solution: {
	 * 		mark: "S",
	 * 		image: solutionIcon,
	 * },
	 */
};

export const getCaseFilterPresentation =
	(filter) => {
		const normalizedFilter =
			normalizeCaseFilter(
				filter,
			);

		return (
			FILTER_PRESENTATION[
				normalizedFilter
			] ?? null
		);
	};

/*
 * ============================================================
 * TOPIC SORTING
 * ============================================================
 *
 * Preferred filters:
 *
 * 1. Fortnite
 * 2. Choiz.io
 * 3. Unreal Engine
 *
 * Remaining filters:
 *
 * alphabetical by their human-readable Sheet label.
 */
export const sortCaseFilterOptions =
	(filters = []) => {
		return [...filters].sort(
			(a, b) => {
				const aPreferredIndex =
					preferredFilterOrder.indexOf(
						a.value,
					);

				const bPreferredIndex =
					preferredFilterOrder.indexOf(
						b.value,
					);

				const aPreferred =
					aPreferredIndex !==
					-1;

				const bPreferred =
					bPreferredIndex !==
					-1;

				if (
					aPreferred &&
					bPreferred
				) {
					return (
						aPreferredIndex -
						bPreferredIndex
					);
				}

				if (aPreferred) {
					return -1;
				}

				if (bPreferred) {
					return 1;
				}

				return a.label.localeCompare(
					b.label,
					undefined,
					{
						sensitivity:
							"base",
					},
				);
			},
		);
	};

/*
 * ============================================================
 * CASE / SLIDE DISPLAY FILTER
 * ============================================================
 *
 * This decides which badge should be DISPLAYED for one case.
 *
 * IMPORTANT:
 *
 * This does NOT modify case.type.
 *
 * case.type remains:
 *
 * work
 * solution
 *
 * and can continue controlling playback / UI behaviour.
 *
 *
 * DISPLAY RULES
 * ------------------------------------------------------------
 *
 * If viewing a specific topic:
 *
 * /cases/fortnite
 *
 * and this case belongs to Fortnite:
 *
 * -> Fortnite
 *
 *
 * If viewing:
 *
 * /cases
 * /cases/work
 * /cases/solutions
 *
 * -> show the first topic from the Sheet "filters" field
 *
 *
 * If the case has no Sheet topic filters:
 *
 * -> fall back to Work / Solutions
 */
export const getCaseDisplayFilter =
	(
		caseItem,
		activeFilter = "all",
	) => {
		const normalizedActiveFilter =
			normalizeCaseFilter(
				activeFilter,
			);

		const caseTopicFilters =
			getCaseFilterOptions(
				caseItem,
			);

		/*
		 * A currently selected topical filter always wins.
		 */
		const activeTopic =
			caseTopicFilters.find(
				(filter) =>
					filter.value ===
					normalizedActiveFilter,
			);

		if (activeTopic) {
			return activeTopic;
		}

		/*
		 * Otherwise use editorial Sheet order.
		 *
		 * "fortnite, realtime, automotive"
		 *
		 * -> Fortnite
		 */
		if (
			caseTopicFilters.length >
			0
		) {
			return caseTopicFilters[
				0
			];
		}

		/*
		 * No topic assigned.
		 *
		 * Fall back to the existing primary category.
		 */
		const normalizedType =
			normalizeCaseFilter(
				caseItem?.type,
			);

		return {
			value:
				normalizedType,

			label:
				getFilterLabel(
					normalizedType,
				),
		};
	};